import { FC, useCallback, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { Column, DataTable } from "components/tables/DataTable"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { CmsSeoRule, CmsSeoRuleTestResult } from "services/cms-seo/type"

import { AsyncList, FilterField, ghostButtonClass, IndexFlag, InfoBox, inputClass, PathText, primaryButtonClass, StatusBadge } from "../CmsUi"
import { SeoRuleFormState, SeoRuleModal } from "../SeoRuleModal"

export const SeoRulesTab: FC = () => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const [rules, setRules] = useState<CmsSeoRule[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [onlyActive, setOnlyActive] = useState<"" | "active" | "paused">("")
  const [formState, setFormState] = useState<SeoRuleFormState>({ kind: "none" })

  const [testUrl, setTestUrl] = useState("")
  const [testResult, setTestResult] = useState<CmsSeoRuleTestResult | null>(null)
  const [testing, setTesting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setRules(await cmsSeoService.listSeoRules())
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const runTest = async () => {
    if (!testUrl.trim()) return
    setTesting(true)
    setTestResult(await cmsSeoService.testSeoRules(testUrl.trim()))
    setTesting(false)
  }

  const needle = search.trim().toLowerCase()
  const visible = rules.filter((rule) => {
    if (onlyActive === "active" && !rule.active) return false
    if (onlyActive === "paused" && rule.active) return false
    if (!needle) return true
    return rule.name.toLowerCase().includes(needle) || rule.url_pattern.toLowerCase().includes(needle)
  })

  const columns: Column<CmsSeoRule>[] = [
    {
      header: t("cmsSeo.rule.priority", { defaultValue: "Priority" }),
      hideBelow: "md",
      render: (rule) => <span className="text-xs text-gray-500 dark:text-gray-400">{rule.priority}</span>
    },
    {
      header: t("cmsSeo.rule.name", { defaultValue: "Rule" }),
      wrap: true,
      render: (rule) => <span className="text-sm">{rule.name}</span>
    },
    { header: t("cmsSeo.rule.pattern", { defaultValue: "URL pattern" }), render: (rule) => <PathText>{rule.url_pattern}</PathText> },
    {
      header: t("cmsSeo.rule.titleTemplate", { defaultValue: "Title template" }),
      hideBelow: "xl",
      render: (rule) => <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{rule.title_template || "—"}</span>
    },
    {
      header: t("cmsSeo.col.index", { defaultValue: "Index" }),
      hideBelow: "lg",
      render: (rule) => <IndexFlag indexed={rule.robots_index} />
    },
    {
      header: t("cmsSeo.rule.matches", { defaultValue: "Matches" }),
      hideBelow: "md",
      render: (rule) => (
        <span className="text-sm">{t("cmsSeo.rule.matchCount", { defaultValue: "{{count}} matches", count: rule.match_count })}</span>
      )
    },
    {
      header: t("cmsSeo.col.status", { defaultValue: "Status" }),
      hideBelow: "sm",
      render: (rule) => <StatusBadge status={rule.active ? "published" : "draft"} />
    },
    {
      header: t("common.actions", { defaultValue: "Actions" }),
      align: "right",
      render: (rule) => (
        <div className="flex justify-end gap-2">
          <button type="button" className={ghostButtonClass} onClick={() => setTestUrl(rule.url_pattern)}>
            {t("cmsSeo.test", { defaultValue: "Test" })}
          </button>
          <button
          type="button"
          className={primaryButtonClass}
          onClick={() => setFormState({ kind: "edit", record: rule })}>
            {t("common.edit", { defaultValue: "Edit" })}
          </button>
        </div>
      )
    }
  ]

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-5">
      <div className="flex flex-wrap items-end gap-4">
        <FilterField label={t("cmsSeo.col.status", { defaultValue: "Status" })}>
          <select className={inputClass} value={onlyActive} onChange={(event) => setOnlyActive(event.target.value as typeof onlyActive)}>
            <option value="">{t("common.all", { defaultValue: "All" })}</option>
            <option value="active">{t("cmsSeo.rule.activeLabel", { defaultValue: "Active" })}</option>
            <option value="paused">{t("cmsSeo.rule.pausedLabel", { defaultValue: "Paused" })}</option>
          </select>
        </FilterField>

        <FilterField label={t("common.search", { defaultValue: "Search" })} grow>
          <input
            className={inputClass}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("cmsSeo.filter.searchRules", { defaultValue: "Search rules or URL patterns…" }) as string}
          />
        </FilterField>

        <button
          type="button"
          className={`${primaryButtonClass} w-full sm:w-auto`}
          onClick={() => setFormState({ kind: "create" })}>
          {t("cmsSeo.createRuleShort", { defaultValue: "+ Create rule" })}
        </button>
      </div>

      <InfoBox>
        {t("cmsSeo.rulesInfo", {
          defaultValue:
            "Rules supply titles, descriptions and indexing for routes the platform generates — sportsbook leagues and events, casino providers and games, blog category listings. The lowest priority number that matches a URL wins."
        })}
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <input
            className={`${inputClass} max-w-md font-mono`}
            value={testUrl}
            onChange={(event) => setTestUrl(event.target.value)}
            placeholder="/casino/game/book-of-dead"
          />
          <button type="button" className={ghostButtonClass} disabled={testing} onClick={runTest}>
            {t("cmsSeo.testUrl", { defaultValue: "Test a URL" })}
          </button>
        </div>
        {testResult && (
          <div className="mt-3 rounded-lg border border-black/[0.06] p-3 dark:border-white/[0.08]">
            {testResult.matched ? (
              <>
                <div className="text-xs font-semibold text-emerald-500">{testResult.rule_name}</div>
                <div className="mt-1 font-mono text-xs">{testResult.title ?? "—"}</div>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{testResult.description ?? "—"}</div>
                <div className="mt-1">
                  <IndexFlag indexed={testResult.robots_index} />
                </div>
              </>
            ) : (
              <span className="text-xs text-amber-500">
                {t("cmsSeo.testNoMatch", { defaultValue: "No rule matches this URL — it falls back to the brand defaults." })}
              </span>
            )}
          </div>
        )}
      </InfoBox>

      <h2 className="text-base font-semibold text-black dark:text-white">
        {t("cmsSeo.rulesHeading", { defaultValue: "SEO rules for generated routes" })}
      </h2>

      <AsyncList
        loading={loading}
        isEmpty={visible.length === 0}
        emptyText={t("cmsSeo.noRules", { defaultValue: "No rules yet — generated routes use the brand defaults." }) as string}
      >
        <DataTable rows={visible} rowKey={(rule) => rule.id} columns={columns} />
      </AsyncList>

      <SeoRuleModal
        state={formState}
        onClose={() => setFormState({ kind: "none" })}
        onSaved={() => {
          setFormState({ kind: "none" })
          load()
        }}
      />
    </div>
  )
}
